import type { ApiMedia, ApiQuestion, ApiQuestionSetting } from '../../../../internalTypes';
import type { ContainerMedia, MediaAttachment } from '../../../../types/shared';
import { getConfigFlag, getConfigValue } from './configUtils';

export function resolveMediaMimeType(asset: ApiMedia): string | undefined {
  if (asset.mediaMimeType) {
    return asset.mediaMimeType;
  }
  if (asset.type === 'PHOTO' || asset.type === 'IMAGE') {
    return 'image/*';
  }
  if (asset.type === 'VIDEO') {
    return 'video/*';
  }
  return asset.type;
}

function resolveAssetUrl(asset: ApiMedia): string | undefined {
  return asset.source || asset.mediaUrl || asset.previewImageUrl;
}

function mapApiAssetToAttachment(asset: ApiMedia, index: number): MediaAttachment | undefined {
  const url = resolveAssetUrl(asset);
  if (!url) {
    return undefined;
  }

  const mimeType = resolveMediaMimeType(asset);
  const altText = asset.alternateText || asset.description;
  const baseId = asset.id ?? url;

  return {
    id: `${baseId}::${index}`,
    url,
    ...(asset.previewImageUrl ? { previewUrl: asset.previewImageUrl } : {}),
    ...(mimeType ? { mimeType } : {}),
    ...(asset.name ? { title: asset.name } : {}),
    ...(altText ? { altText } : {}),
  };
}

export function mapContainerMedia(
  raw: ApiQuestion,
  configs: ApiQuestionSetting[]
): ContainerMedia | undefined {
  const primaryAsset = raw.questionContainerAssets?.[0];
  const url = primaryAsset ? resolveAssetUrl(primaryAsset) : undefined;
  const mimeType = primaryAsset ? resolveMediaMimeType(primaryAsset) : undefined;
  const previewUrl = primaryAsset?.previewImageUrl;
  const title = primaryAsset?.name || primaryAsset?.caption;

  const alignment = getConfigValue(configs, 'QUESTION_CONTAINER_MEDIA_ALIGNMENT');
  const sizeRaw = getConfigValue(configs, 'TEXT_AND_MEDIA_SIZE');
  const size = sizeRaw !== undefined ? Number(sizeRaw) : undefined;
  const altText = getConfigValue(configs, 'TEXT_AND_MEDIA_ALT_TEXT');

  if (
    !url &&
    !mimeType &&
    !previewUrl &&
    !alignment &&
    size === undefined &&
    !altText &&
    !title
  ) {
    return undefined;
  }

  return {
    ...(url ? { url } : {}),
    ...(previewUrl ? { previewUrl } : {}),
    ...(mimeType ? { mimeType } : {}),
    ...(alignment ? { alignment } : {}),
    ...(size !== undefined && !Number.isNaN(size) ? { size } : {}),
    ...(altText ? { altText } : {}),
    ...(title ? { title } : {}),
  };
}

export function mapMediaAttachments(
  raw: ApiQuestion,
  _configs: ApiQuestionSetting[]
): MediaAttachment[] | undefined {
  if (raw.answerType === 'HEATMAP') {
    return undefined;
  }

  const assets = raw.questionAssets;
  if (!Array.isArray(assets) || assets.length === 0) {
    return undefined;
  }

  const attachments = assets
    .map((asset, index) => mapApiAssetToAttachment(asset, index))
    .filter((attachment): attachment is MediaAttachment => attachment !== undefined);

  return attachments.length > 0 ? attachments : undefined;
}

export function mapMediaFeatureFlags(configs: ApiQuestionSetting[]): {
  enablePreview?: boolean;
  enableDownload?: boolean;
} {
  const enablePreview = getConfigFlag(configs, 'MEDIA_PREVIEW');
  const enableDownload = getConfigFlag(configs, 'MEDIA_DOWNLOAD');

  return {
    ...(enablePreview ? { enablePreview: true } : {}),
    ...(enableDownload ? { enableDownload: true } : {}),
  };
}
